import React, { useEffect, useRef } from 'react';
import './EnhancedHTMLRenderer.css';

interface EnhancedHTMLRendererProps {
  content: string;
  className?: string;
}

const EnhancedHTMLRenderer: React.FC<EnhancedHTMLRendererProps> = ({
  content,
  className = ''
}) => {
  const containerRef = useRef<HTMLDivElement>(null);

  // 检测并提取Mermaid图表代码
  const extractMermaidCharts = (html: string): { cleanedHTML: string; charts: Array<{ id: string; code: string }> } => {
    const charts: Array<{ id: string; code: string }> = [];
    
    // 匹配 <pre class="mermaid"> ... </pre> 格式
    const mermaidRegex = /<pre\s+class="mermaid"[^>]*>([\s\S]*?)<\/pre>/gi;
    
    let cleanedHTML = html;
    let match;
    let chartIndex = 0;
    
    while ((match = mermaidRegex.exec(html)) !== null) {
      const chartCode = match[1].trim();
      const chartId = `mermaid-chart-${chartIndex++}`;
      
      // 提取图表代码
      charts.push({
        id: chartId,
        code: chartCode
      });
      
      // 用占位符替换原始的mermaid代码
      cleanedHTML = cleanedHTML.replace(match[0], `<div data-mermaid-placeholder="${chartId}"></div>`);
    }
    
    return { cleanedHTML, charts };
  };

  // 清理和增强HTML内容
  const enhanceHTML = (html: string): { enhancedHTML: string; mermaidCharts: Array<{ id: string; code: string }> } => {
    if (!html) return { enhancedHTML: '', mermaidCharts: [] };

    // 按照CLAUDE.md指南处理AI生成的特殊标签
    let cleanedHTML = html;
    
    // 检查是否存在AI生成的特殊标签
    if (html.includes('===HTML_RESULT_START===') && html.includes('===HTML_RESULT_END===')) {
      // 提取HTML_RESULT区域的内容
      const htmlStartIndex = html.indexOf('===HTML_RESULT_START===') + '===HTML_RESULT_START==='.length;
      const htmlEndIndex = html.indexOf('===HTML_RESULT_END===');
      if (htmlEndIndex > htmlStartIndex) {
        cleanedHTML = html.substring(htmlStartIndex, htmlEndIndex).trim();
      }
    }
    
    // 移除可能存在的THINKING区域
    if (cleanedHTML.includes('===THINKING_START===') && cleanedHTML.includes('===THINKING_END===')) {
      cleanedHTML = cleanedHTML.replace(/===THINKING_START===[\s\S]*?===THINKING_END===/g, '').trim();
    }

    // 移除可能的markdown代码块标记
    cleanedHTML = cleanedHTML.replace(/^```html\s*\n?|\n?```\s*$/g, '').trim();
    cleanedHTML = cleanedHTML.replace(/^```\s*\n?|\n?```\s*$/g, '').trim();

    // 提取Mermaid图表
    const { cleanedHTML: htmlWithoutMermaid, charts } = extractMermaidCharts(cleanedHTML);
    cleanedHTML = htmlWithoutMermaid;

    // 确保所有样式都是内联的，并添加响应式支持
    cleanedHTML = cleanedHTML.replace(
      /style="([^"]*)"/g,
      (match, styles) => {
        // 添加响应式支持
        const enhancedStyles = styles
          .replace(/grid-template-columns:\s*repeat\(auto-fit,\s*minmax\((\d+)px,\s*1fr\)\)/g, 
            'grid-template-columns: repeat(auto-fit, minmax(min(100%, $1px), 1fr))')
          .replace(/max-width:\s*100%/g, 'max-width: 100%; width: 100%');
        
        return `style="${enhancedStyles}"`;
      }
    );

    // 添加响应式容器包装
    if (!cleanedHTML.includes('responsive-container')) {
      cleanedHTML = `<div class="responsive-container">${cleanedHTML}</div>`;
    }

    return { enhancedHTML: cleanedHTML, mermaidCharts: charts };
  };

  const { enhancedHTML, mermaidCharts } = enhanceHTML(content);

  // 处理Mermaid图表占位符
  const renderHTMLWithMermaid = () => {
    let finalHTML = enhancedHTML;
    
    mermaidCharts.forEach(chart => {
      const placeholder = `<div data-mermaid-placeholder="${chart.id}"></div>`;
      const mermaidComponent = `<div class="mermaid-chart-container" data-chart-id="${chart.id}"></div>`;
      finalHTML = finalHTML.replace(placeholder, mermaidComponent);
    });

    return finalHTML;
  };

  // 在组件挂载后渲染Mermaid图表
  useEffect(() => {
    if (!containerRef.current || mermaidCharts.length === 0) return;

    const renderMermaidCharts = () => {
      for (const chart of mermaidCharts) {
        const chartContainer = containerRef.current?.querySelector(`[data-chart-id="${chart.id}"]`);
        if (chartContainer) {
          // 直接渲染Mermaid图表，不使用React组件
          const chartDiv = document.createElement('div');
          chartDiv.className = 'mermaid-chart my-4';
          chartContainer.appendChild(chartDiv);
          
          // 创建MermaidChart实例
          renderSingleChart(chartDiv, chart.code);
        }
      }
    };

    const renderSingleChart = async (container: HTMLElement, chartCode: string) => {
      try {
        // 使用CDN方式加载Mermaid
        if (typeof window !== 'undefined' && !(window as any).mermaid) {
          // 动态加载Mermaid CDN
          const script = document.createElement('script');
          script.type = 'module';
          script.innerHTML = `
            import mermaid from 'https://cdn.jsdelivr.net/npm/mermaid@10/dist/mermaid.esm.min.js';
            window.mermaid = mermaid;
            window.mermaid.initialize({
              startOnLoad: false,
              theme: 'default',
              securityLevel: 'loose',
              flowchart: {
                useMaxWidth: true,
                htmlLabels: true,
                curve: 'basis'
              },
              themeVariables: {
                primaryColor: '#3b82f6',
                primaryTextColor: '#1f2937',
                primaryBorderColor: '#2563eb',
                lineColor: '#6b7280',
                sectionBkgColor: '#f8fafc',
                altSectionBkgColor: '#f1f5f9',
                gridColor: '#e5e7eb',
                secondaryColor: '#f3f4f6',
                tertiaryColor: '#ffffff'
              }
            });
            window.mermaidReady = true;
          `;
          document.head.appendChild(script);
          
          // 等待加载完成
          let attempts = 0;
          const checkMermaid = () => {
            attempts++;
            if ((window as any).mermaidReady && (window as any).mermaid) {
              renderChart();
            } else if (attempts < 50) {
              setTimeout(checkMermaid, 100);
            } else {
              showError();
            }
          };
          checkMermaid();
        } else if ((window as any).mermaid) {
          renderChart();
        } else {
          showError();
        }

        async function renderChart() {
          try {
            const mermaid = (window as any).mermaid;
            const chartId = `mermaid-${Math.random().toString(36).substr(2, 9)}`;
            const { svg } = await mermaid.render(chartId, chartCode);
            
            container.innerHTML = svg;
            
            // 添加响应式支持
            const svgElement = container.querySelector('svg');
            if (svgElement) {
              svgElement.style.maxWidth = '100%';
              svgElement.style.height = 'auto';
              svgElement.setAttribute('preserveAspectRatio', 'xMidYMid meet');
            }
          } catch (error) {
            console.error('Mermaid 渲染失败:', error);
            showError();
          }
        }

        function showError() {
          container.innerHTML = `
            <div class="flex items-center justify-center p-8 bg-red-50 border border-red-200 rounded-lg">
              <div class="text-center">
                <p class="text-red-700 font-medium mb-2">流程图渲染失败</p>
                <p class="text-red-600 text-sm">请检查图表语法或刷新页面重试</p>
              </div>
            </div>
          `;
        }
      } catch (error) {
        console.error('Mermaid 加载失败:', error);
        container.innerHTML = `
          <div class="flex items-center justify-center p-8 bg-yellow-50 border border-yellow-200 rounded-lg">
            <div class="text-center">
              <p class="text-yellow-700 font-medium mb-2">流程图加载中...</p>
              <p class="text-yellow-600 text-sm">如果长时间未显示，请刷新页面</p>
            </div>
          </div>
        `;
      }
    };

    // 延迟渲染以确保DOM已更新
    const timer = setTimeout(renderMermaidCharts, 100);
    return () => clearTimeout(timer);
  }, [content, mermaidCharts]);

  return (
    <div 
      ref={containerRef}
      className={`enhanced-html-renderer ${className}`}
      dangerouslySetInnerHTML={{ __html: renderHTMLWithMermaid() }}
    />
  );
};

export default EnhancedHTMLRenderer;